<template>
  <div
    class="form-actions"
    ref="formActions"
  >
    <div>
      <Button
        v-if="actionButtonsProps('preview').visible"
        ph-autocapture
        plain
        type="warning"
        @click="handleOpenPreview()"
        icon="icon icon-eye"
        data-test="preview_invoice"
        class="button-preview"
      >
        {{ $t('form.invoiceForm.buttons.preview') }}
      </Button>
    </div>
    <div>
      <Checkbox
        v-if="actionButtonsProps('eetEvidence').visible"
        v-model="register_eet"
        class="eet-checker"
        :disabled="actionButtonsProps('eetEvidence').disabled"
        data-test="eet-registered"
      >
        <strong>{{ $t('form.invoiceForm.fields.eetSettings.eetEvidence') }}</strong>
      </Checkbox>
      <Button
        class="submit-button"
        @click="showSubmitDialog()"
        v-if="actionButtonsProps('submit').visible"
        type="warning"
        icon="icon icon-send"
        data-test="show_submit_dialog"
        plain
      >
        {{ $t(`form.invoiceForm.buttons.sendByEmail${buttonPostfix}`) }}
      </Button>
      <Button
        class="submit-button"
        v-if="actionButtonsProps('download').visible"
        @click="handleDownload()"
        type="warning"
        icon="icon icon-download"
        data-test="download_invoice"
      >
        {{ $t('form.invoiceForm.buttons.downloadOnly') }}
      </Button>
      <Button
        class="submit-button"
        v-if="actionButtonsProps('correctionInvoice').visible"
        @click="elInvoiceForm.correctionInvoiceRedirect"
        type="warning"
        icon="icon icon-pencil"
        data-test="correction_invoice"
        plain
      >
        {{ $t('form.invoiceForm.buttons.correction_invoice') }}
      </Button>
      <Button
        class="submit-button"
        v-if="actionButtonsProps('save').visible"
        @click="submit('save')"
        type="warning"
        icon="icon icon-save"
        data-test="save_invoice"
        plain
      >
        {{ $t(`form.invoiceForm.buttons.save${buttonPostfix}`) }}
      </Button>
      <Button
        class="submit-button"
        v-if="actionButtonsProps('saveAndDownload').visible"
        @click="submit('saveAndDownload')"
        type="warning"
        icon="icon icon-download"
        data-test="save_download_invoice"
      >
        {{ $t(`form.invoiceForm.buttons.download${buttonPostfix}`) }}
      </Button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import { scrollToErrorField } from '~/lib/validators/scrollToErrorField';
import bindInvoiceAffix from '~/lib/affix';
import { PATH_DIRECT_EXTEND, PATH_CONTACT, PATH_INVOICES_OVERVIEW } from '~/router';

export default {
  name: 'ActionButtons',
  inject: ['elForm', 'elInvoiceForm', 'invoice', 'invoiceType'],
  data: () => ({
    contactId: null,
    buttonLoading: {
      save: false,
      saveAndDownload: false,
    },
  }),
  computed: {
    ...mapGetters('auth', ['isAuthorized', 'userHasPremiumPlan', 'userHasPlan']),
    ...mapState('config', {
      isEetEnabled: state => state.subscription['eet_enabled?'],
    }),
    ...mapState('invoiceForm', ['recurring_draft']),
    ...mapFields('invoiceForm', ['register_eet', 'invoice', 'state']),
    isRecurringDraft() {
      return this.recurring_draft;
    },
    buttonPostfix() {
      return (this.state === 'proposal' || this.invoiceType === 'new') ? 'Proposal' : '';
    },
  },
  methods: {
    actionButtonsProps(button) {
      const props = {
        sendByEmail: { visible: true },
        correctionInvoice: { visible: this.elInvoiceForm.isEditEetInvoiceDisabled },
        download: {
          visible:
                !this.userHasPremiumPlan
                || (this.elInvoiceForm.isEditEetInvoiceDisabled && this.userHasPremiumPlan),
        },
        save: {
          visible:
                this.isAuthorized
                && !this.elInvoiceForm.isEditEetInvoiceDisabled
                && this.userHasPremiumPlan,
        },
        submit: {
          visible:
                this.isAuthorized
                && !this.elInvoiceForm.isEditEetInvoiceDisabled
                && this.userHasPlan
                && !this.isRecurringDraft,
        },
        preview: {
          visible:
                this.isAuthorized
                && !this.elInvoiceForm.isEditEetInvoiceDisabled
                && !this.isRecurringDraft,
        },
        saveAndDownload: {
          visible:
                this.isAuthorized
                && !this.elInvoiceForm.isEditEetInvoiceDisabled
                && this.userHasPremiumPlan
                && !this.isRecurringDraft,
        },
        eetEvidence: {
          visible: this.isEetEnabled,
          disabled: this.elInvoiceForm.isEditEetInvoiceDisabled,
        },
      };
      return props[button];
    },
    handleOpenPreview() {
      this.elForm.validate((valid) => {
        if (valid) {
          if (this.checkUserPlan()) {
            const dispatch = () => this.$store.dispatch('invoiceForm/previewInvoice', { action: 'preview' });
            setTimeout(dispatch, 200);
          }
        } else {
          scrollToErrorField(this.elForm);
        }
      });
    },
    showSubmitDialog() {
      this.elForm.validate((valid) => {
        if (valid) {
          if (this.checkUserPlan()) {
            this.$store.commit('submitInvoiceForm/setInvoiceNumber', this.invoice.number);
            const dispatch = () => this.$store.dispatch('submitInvoiceForm/buildForm');
            setTimeout(dispatch, 200);
          }
        } else {
          scrollToErrorField(this.elForm);
        }
      });
    },
    checkUserPlan() {
      if (!this.isAuthorized) {
        this.$store.dispatch('flashMessages/addMessage', {
          kind: 'info',
          key: 'notAuhorized',
        });
        this.$store.dispatch('uiEffects/scrollTo', {
          top: 0,
          left: 0,
        });
        return false;
      }
      if (!this.userHasPlan) {
        this.$store.dispatch('flashMessages/addMessage', {
          kind: 'error',
          key: 'inactiveSubscription',
          button: {
            key: 'buttons.extendExpired',
            action: this.extendSubscription,
          },
        });
        this.$store.dispatch('uiEffects/scrollTo', {
          top: 0,
          left: 0,
        });
        return false;
      }
      return true;
    },
    submit(action) {
      this.elForm.validate((valid) => {
        if (valid) {
          if (this.checkUserPlan()) {
            this.buttonLoading[action] = true;
            this.$store.dispatch('invoiceForm/createInvoice', {
              action,
              contactId: this.contactId,
            }).then(() => {
              if (this.contactId) {
                this.$router.push({
                  name: PATH_CONTACT,
                  params: { contactId: this.contactId },
                });
              } else {
                this.$router.push({ name: PATH_INVOICES_OVERVIEW });
              }
            }).finally(() => {
              this.buttonLoading[action] = false;
            });
          }
          return true;
        }
        scrollToErrorField(this.elForm);
        this.buttonLoading[action] = false;
        return false;
      });
    },
    handleDownload() {
      if (this.elInvoiceForm.isEditEetInvoiceDisabled) {
        this.$store.dispatch('invoiceForm/downloadInvoice', {
          id: this.invoice.id,
          onSuccess: ['file/downloadFile'],
        });
      } else {
        this.elForm.validate((valid) => {
          if (valid) {
            if (!this.isAuthorized) {
              // eslint-disable-next-line no-unused-expressions
              this.$posthog?.capture('unregistered_download_invoice');
              this.$store.dispatch('auth/openDialog');
            } else if (this.checkUserPlan()) {
              this.$store.dispatch('invoiceForm/createInvoice', {
                action: 'download',
                contactId: this.contactId,
              }).then(() => {
                if (this.contactId) {
                  this.$router.push({
                    name: PATH_CONTACT,
                    params: { contactId: this.contactId },
                  });
                } else {
                  this.$router.push({ name: PATH_INVOICES_OVERVIEW });
                }
              });
            }
          } else {
            scrollToErrorField(this.elForm);
          }
        });
      }
    },
    setAfterActionRedirect() {
      if (typeof this.$route.query.contactId !== 'undefined') {
        this.contactId = parseInt(this.$route.query.contactId, 10);
      }
    },
    extendSubscription() {
      this.$router.push({
        name: PATH_DIRECT_EXTEND,
      });
    },
  },
  mounted() {
    this.setAfterActionRedirect();
    bindInvoiceAffix(this.$refs.formActions);
  },
};
</script>

<style lang="scss" scoped>
.form-actions {
  margin: 0 -25px;
  padding: 15px 25px;
  background: $--color-white;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  transform: translateY(0);
  transition: transform .3s ease-in-out;
  z-index: $--index-top;

  @media only screen and (min-width: $--breakpoint-sm) {
    position: sticky;
    bottom: 0;
  }

  @media only screen and (width <= 800px) {
    display: block;
  }

  @media only screen and (max-width: $--breakpoint-sm - 1px) {
    margin-left: math.div($grid-gutter-width, -2);
    margin-right: math.div($grid-gutter-width, -2);
    padding-left: math.div($grid-gutter-width, 2);
    padding-right: math.div($grid-gutter-width, 2);
  }

  &.affixed {
    @media only screen and (min-width: $--breakpoint-sm) {
      &::before {
        content: '';
        height: 15px;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 100%;
        background-image: linear-gradient(180deg, transparent 0%, $--color-black 100%, $--color-white 100%);
        opacity: .05;
        pointer-events: none;
        transition: .3s ease;
      }
    }
  }

  &.not-visible {
    @media only screen and (min-width: $--breakpoint-sm) {
      transform: translateY(100%);

      &::before {
        opacity: 0;
      }
    }
  }
}

.button-preview {
  @media only screen and (width <= 800px) {
    margin-bottom: 12px;
  }

  @media only screen and (width <= 600px) {
    width: 100%;
    margin-bottom: 0;
  }
}

.eet-checker {
  display: inline-block;
  margin: auto 10px;

  @media only screen and (width <= 800px) {
    margin: 12px 0 22px;
    display: block;
  }

  @media only screen and (width <= 600px) {
    display: inline-block;
  }
}

.submit-button {
  @media only screen and (width <= 600px) {
    margin: 10px 0 0 !important;
    width: 100%;
  }
}
</style>
